import { useCallback, useRef } from 'react';
import { observe } from 'react-intersection-observer';
import useUnmount from 'react-use/lib/useUnmount';

import {
  ScrollEndFallbackEvent,
  useScrollEndFallback,
} from './useScrollEndFallback';

type UseSectionObserverOptions = {
  onSectionInView: (id: string) => void;
  onScrollEndFallback: (event: ScrollEndFallbackEvent) => void;
};

type ObservedSection = {
  element: HTMLElement;
  unobserve: () => void;
};

type SectionObserver = {
  getSectionElement: (id: string) => HTMLElement | undefined;
  observeSectionElement: (id: string, element: HTMLElement | null) => void;
};

const activeSectionMarker = {
  heightPercent: 1,
  offsetPercent: 20,
} as const;
const observerOptions = {
  rootMargin: `-${activeSectionMarker.offsetPercent}% 0px -${
    100 - activeSectionMarker.offsetPercent - activeSectionMarker.heightPercent
  }% 0px`,
} as const;

export const useSectionObserver = ({
  onSectionInView,
  onScrollEndFallback,
}: UseSectionObserverOptions): SectionObserver => {
  const registrationsRef = useRef(new Map<string, ObservedSection>());
  const sectionIdsAtMarkerRef = useRef(new Set<string>());
  const registrations = registrationsRef.current;
  const sectionIdsAtMarker = sectionIdsAtMarkerRef.current;

  const scrollEndFallbackActiveRef = useScrollEndFallback({
    onFallback: onScrollEndFallback,
    registrations,
    sectionIdsAtMarker,
  });

  const observeSectionElement = useCallback(
    (sectionId: string, element: HTMLElement | null): void => {
      registrations.get(sectionId)?.unobserve();
      registrations.delete(sectionId);
      sectionIdsAtMarker.delete(sectionId);

      if (!element) return;

      const unobserve = observe(
        element,
        inView => {
          if (!inView) {
            sectionIdsAtMarker.delete(sectionId);
            return;
          }

          sectionIdsAtMarker.add(sectionId);
          if (!scrollEndFallbackActiveRef.current) {
            onSectionInView(sectionId);
          }
        },
        observerOptions,
        false,
      );

      registrations.set(sectionId, { element, unobserve });
    },
    [
      onSectionInView,
      registrations,
      scrollEndFallbackActiveRef,
      sectionIdsAtMarker,
    ],
  );

  useUnmount(() => {
    registrations.forEach(({ unobserve }) => unobserve());
    registrations.clear();
    sectionIdsAtMarker.clear();
  });

  const getSectionElement = useCallback(
    (sectionId: string): HTMLElement | undefined =>
      registrations.get(sectionId)?.element,
    [registrations],
  );

  return { getSectionElement, observeSectionElement };
};
